import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import StudentHeader from '@/components/student/StudentHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Building2, 
  Globe, 
  BookOpen, 
  CheckCircle, 
  Clock,
  IndianRupee,
  ArrowRight,
  Search,
  AlertCircle,
  Award,
  MapPin,
  Filter,
  ClipboardCheck,
  Wallet,
  CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Eligibility labels
const eligibilityLabels = {
  '10th': '10th Pass',
  '12th': '12th Pass',
  'graduation': 'Graduation',
  'post_graduation': 'Post Graduation'
};

// Qualification levels for comparison
const qualificationLevels = {
  '10th': 1,
  '12th': 2,
  'graduation': 3,
  'post_graduation': 4
};

const AdmissionPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [studentEducation, setStudentEducation] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  
  // Center search/filter states
  const [centerSearchTerm, setCenterSearchTerm] = useState('');
  const [centerStateFilter, setCenterStateFilter] = useState('all');
  const [centerDistrictFilter, setCenterDistrictFilter] = useState('all');
  
  const [admissionData, setAdmissionData] = useState({
    course_id: '',
    course_name: '',
    course_fee: 0,
    original_fee: 0,
    training_mode: '',  // STT or RPL - mandatory selection
    course_type: '',
    enrollment_mode: 'online',
    center_id: '',
    center_name: '',
    // Fee Installment fields
    installment_plan: 'one', // 'one' or 'two'
    installment_1_amount: 0,
    installment_2_amount: 0
  });

  // Calculate installment amounts when fee or plan changes
  const calculateInstallments = (fee, plan) => {
    if (plan === 'two') {
      const inst1 = Math.round(fee * 0.6); // 60%
      const inst2 = fee - inst1; // 40% (remaining)
      return { installment_1_amount: inst1, installment_2_amount: inst2 };
    }
    return { installment_1_amount: fee, installment_2_amount: 0 };
  };

  // Check if installment option should be shown (STT + Professional category only)
  const showInstallmentOption = admissionData.training_mode === 'STT' && 
    (admissionData.course_category === 'professional' || admissionData.course_type === 'professional');

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to take admission');
      navigate('/auth/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [coursesRes, centersRes, educationRes, profileRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/public/centers`),
        axios.get(`${API}/student/education`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/profile/student`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setCourses(coursesRes.data || []);
      setCenters(centersRes.data || []);
      setStudentEducation(educationRes.data);
      setStudentProfile(profileRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if student is eligible for a course
  const checkEligibility = (course) => {
    const minQual = course.minimum_qualification || '10th';
    const studentQual = studentEducation?.highest_qualification || '';
    
    const requiredLevel = qualificationLevels[minQual.toLowerCase()] || 1;
    const studentLevel = qualificationLevels[studentQual.toLowerCase()] || 0;
    
    return {
      isEligible: studentLevel >= requiredLevel,
      requiredQualification: minQual,
      studentQualification: studentQual
    };
  };

  // Get unique states and districts from centers for filters
  const uniqueStates = useMemo(() => {
    return [...new Set(centers.map(c => c.state).filter(Boolean))].sort();
  }, [centers]);

  const uniqueDistricts = useMemo(() => {
    if (centerStateFilter === 'all') {
      return [...new Set(centers.map(c => c.district).filter(Boolean))].sort();
    }
    return [...new Set(centers.filter(c => c.state === centerStateFilter).map(c => c.district).filter(Boolean))].sort();
  }, [centers, centerStateFilter]);

  // Sort and filter centers based on student location and search
  const sortedAndFilteredCenters = useMemo(() => {
    const studentDistrict = studentProfile?.district?.toLowerCase() || '';
    const studentState = studentProfile?.state?.toLowerCase() || '';
    
    // First, filter centers
    let filtered = centers.filter(center => {
      // Search filter
      const matchesSearch = centerSearchTerm === '' || 
        (center.center_name || '').toLowerCase().includes(centerSearchTerm.toLowerCase()) ||
        (center.city || '').toLowerCase().includes(centerSearchTerm.toLowerCase()) ||
        (center.center_code || '').toLowerCase().includes(centerSearchTerm.toLowerCase());
      
      // State filter
      const matchesState = centerStateFilter === 'all' || center.state === centerStateFilter;
      
      // District filter
      const matchesDistrict = centerDistrictFilter === 'all' || center.district === centerDistrictFilter;
      
      return matchesSearch && matchesState && matchesDistrict;
    });
    
    // Then sort by proximity to student
    filtered.sort((a, b) => {
      const aDistrict = (a.district || '').toLowerCase();
      const bDistrict = (b.district || '').toLowerCase();
      const aState = (a.state || '').toLowerCase();
      const bState = (b.state || '').toLowerCase();
      
      // Priority 1: Same district as student
      const aInSameDistrict = aDistrict === studentDistrict && aState === studentState;
      const bInSameDistrict = bDistrict === studentDistrict && bState === studentState;
      
      if (aInSameDistrict && !bInSameDistrict) return -1;
      if (!aInSameDistrict && bInSameDistrict) return 1;
      
      // Priority 2: Same state as student
      const aInSameState = aState === studentState;
      const bInSameState = bState === studentState;
      
      if (aInSameState && !bInSameState) return -1;
      if (!aInSameState && bInSameState) return 1;
      
      // Priority 3: Alphabetically by state, then district
      if (aState !== bState) return aState.localeCompare(bState);
      return aDistrict.localeCompare(bDistrict);
    });
    
    return filtered;
  }, [centers, studentProfile, centerSearchTerm, centerStateFilter, centerDistrictFilter]);

  const handleCourseSelect = (course, trainingMode = null, calculatedFee = null) => {
    // Check if profile is complete first
    if (!studentEducation?.highest_qualification) {
      toast.info('Please complete your profile and add education details first', {
        duration: 3000
      });
      navigate('/student/profile');
      return;
    }
    
    // Check eligibility before allowing selection
    const eligibility = checkEligibility(course);
    
    if (!eligibility.isEligible) {
      toast.error(`You need ${eligibilityLabels[eligibility.requiredQualification] || eligibility.requiredQualification} to enroll in this course. Your current qualification: ${eligibilityLabels[eligibility.studentQualification] || 'Not specified'}`);
      return;
    }
    
    // Check if course is Degree or Diploma - skip STT/RPL selection for these
    // Check both course_type and category since data may have category='diploma' with course_type='paid'
    const isDegreeDiploma = course.course_type === 'degree' || course.course_type === 'diploma' ||
                           course.category === 'degree' || course.category === 'diploma' ||
                           course.name?.toLowerCase().includes('degree') ||
                           course.name?.toLowerCase().includes('diploma') ||
                           course.name?.toLowerCase().includes('pgdca') ||
                           course.name?.toLowerCase().includes('bca') ||
                           course.name?.toLowerCase().includes('bba') ||
                           course.name?.toLowerCase().includes('mba') ||
                           course.name?.toLowerCase().includes('mca') ||
                           course.name?.toLowerCase().includes('b.com') ||
                           course.name?.toLowerCase().includes('m.com') ||
                           course.name?.toLowerCase().includes('b.a') ||
                           course.name?.toLowerCase().includes('m.a') ||
                           course.name?.toLowerCase().includes('m.sc') ||
                           course.name?.toLowerCase().includes('d.voc') ||
                           course.name?.toLowerCase().includes('b.voc');
    
    if (isDegreeDiploma) {
      // For Degree/Diploma courses - skip training mode step, go directly to enrollment mode
      setAdmissionData({
        ...admissionData,
        course_id: course.id,
        course_name: course.name,
        original_fee: course.fee,
        course_fee: course.fee,
        training_mode: 'N/A', // Not applicable for Degree/Diploma
        course_type: course.course_type,
        course_duration: course.duration,
        course_category: course.category
      });
      setStep(2); // Go directly to enrollment mode selection
      return;
    }
    
    // For Professional courses - If training mode not selected, go to mode selection step
    if (!trainingMode) {
      setAdmissionData({
        ...admissionData,
        course_id: course.id,
        course_name: course.name,
        original_fee: course.fee,
        course_fee: course.fee, // Will be updated based on mode
        training_mode: '',
        course_type: course.course_type,
        course_duration: course.duration,
        course_category: course.category
      });
      setStep(1.5); // New step for STT/RPL selection
      return;
    }

    // Training mode already selected
    setAdmissionData({
      ...admissionData,
      course_id: course.id,
      course_name: course.name,
      original_fee: course.fee,
      course_fee: calculatedFee || course.fee,
      training_mode: trainingMode,
      course_type: course.course_type,
      course_duration: course.duration,
      course_category: course.category
    });
    setStep(2);
  };

  const handleModeSelect = (mode, center = null) => {
    const updatedData = {
      ...admissionData,
      enrollment_mode: mode,
      center_id: center?.id || '',
      center_name: center ? (center.center_name || center.name || center.owner_name || '') : '',
      center_code: center?.center_code || ''
    };
    
    setAdmissionData(updatedData);
    
    // Check if installment option should be shown (STT + Professional category only)
    const isProfessional = updatedData.course_category === 'professional' || updatedData.course_type === 'professional';
    if (updatedData.training_mode === 'STT' && isProfessional) {
      // Go to installment selection step
      setStep(2.5);
    } else {
      setStep(3);
    }
  };

  // Handle installment plan selection
  const handleInstallmentSelect = (plan) => {
    const installments = calculateInstallments(admissionData.course_fee, plan);
    setAdmissionData({
      ...admissionData,
      installment_plan: plan,
      ...installments
    });
    setStep(3);
  };

  const handleSubmitAdmission = async () => {
    setSubmitting(true);
    
    // Demo students list - bypass payment for these
    const demoEmails = ['demo_test@iqra.com', 'test_student@iqra.com', 'demo@iqra.com'];
    const isDemoStudent = user?.email && demoEmails.includes(user.email.toLowerCase());
    
    // Calculate installment amounts for STT + Professional
    const isInstallmentEnabled = showInstallmentOption && admissionData.installment_plan === 'two';
    const installment1 = isInstallmentEnabled ? Math.round(admissionData.course_fee * 0.6) : admissionData.course_fee;
    const installment2 = isInstallmentEnabled ? (admissionData.course_fee - installment1) : 0;
    
    try {
      const response = await axios.post(`${API}/admissions/create`, {
        course_id: admissionData.course_id,
        course_name: admissionData.course_name,
        course_fee: admissionData.course_fee,
        original_fee: admissionData.original_fee,
        training_mode: admissionData.training_mode,
        enrollment_mode: admissionData.enrollment_mode,
        center_id: admissionData.center_id || null,
        center_name: admissionData.center_name || null,
        center_code: admissionData.center_code || null,
        // Installment info
        installment_plan: showInstallmentOption ? admissionData.installment_plan : 'one',
        installment_1_amount: installment1,
        installment_2_amount: installment2,
        installment_1_status: 'pending',
        installment_2_status: isInstallmentEnabled ? 'pending' : 'not_applicable'
      });
      
      // Demo student - skip payment, mark as paid directly
      if (isDemoStudent) {
        // Mark admission as paid via API
        try {
          await axios.post(`${API}/admissions/${response.data.admission_id}/mark-demo-paid`);
        } catch (e) {
          // If API doesn't exist, still proceed
          console.log('Demo payment marking:', e);
        }
        toast.success('Demo Enrollment Successful! Course activated.');
        setSubmitting(false);
        navigate('/student/dashboard?tab=admissions');
        return;
      }
      
      // If paid course, initiate Razorpay payment directly
      if (admissionData.course_fee > 0) {
        // For installment plan, only charge first installment now
        const payNowAmount = isInstallmentEnabled ? installment1 : admissionData.course_fee;
        toast.success('Admission confirmed! Initiating payment...');
        initiatePayment(response.data.admission_id, payNowAmount, admissionData.course_name, isInstallmentEnabled);
      } else {
        toast.success('Admission successful! Redirecting to dashboard...');
        navigate('/student/dashboard?tab=admissions');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admission failed');
      setSubmitting(false);
    }
  };

  // Razorpay Payment Integration
  const initiatePayment = async (admissionId, amount, courseName, isInstallment = false) => {
    try {
      // Create Razorpay order
      const orderResponse = await axios.post(`${API}/payment/create-admission-order`, {
        admission_id: admissionId,
        amount: amount,
        is_installment: isInstallment,
        installment_number: 1
      });

      const { order_id, amount: orderAmount, currency } = orderResponse.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderAmount,
        currency: currency || 'INR',
        name: 'Iqra Skills',
        description: isInstallment 
          ? `${courseName} - Installment 1 (60%)` 
          : `Admission Fee - ${courseName}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API}/payment/verify-admission`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              admission_id: admissionId,
              is_installment: isInstallment,
              installment_number: 1
            });
            if (isInstallment) {
              toast.success('Installment 1 paid successfully! Welcome to Iqra Skills!');
            } else {
              toast.success('Payment successful! Welcome to Iqra Skills!');
            }
            navigate('/student/dashboard?tab=admissions');
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.mobile || ''
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled. You can pay later from your dashboard.');
            setSubmitting(false);
            navigate('/student/dashboard?tab=admissions');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Failed to initiate payment. Please try from dashboard.');
      setSubmitting(false);
      navigate('/student/dashboard?tab=admissions');
    }
  };

  // Filter courses - exclude free courses (fee must be > 0)
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || course.course_type === filterType;
    const matchesCategory = filterCategory === 'all' || course.category === filterCategory;
    const isPaidCourse = course.fee > 0; // Only show paid courses
    return matchesSearch && matchesType && matchesCategory && isPaidCourse;
  });

  // Check if selected course is Degree or Diploma (only Distance Mode available)
  const isDegreeOrDiploma = admissionData.training_mode === 'N/A' ||
                           admissionData.course_type === 'degree' || 
                           admissionData.course_type === 'diploma' ||
                           admissionData.course_category === 'degree' ||
                           admissionData.course_category === 'diploma' ||
                           admissionData.course_name?.toLowerCase().includes('degree') ||
                           admissionData.course_name?.toLowerCase().includes('diploma') ||
                           admissionData.course_name?.toLowerCase().includes('pgdca') ||
                           admissionData.course_name?.toLowerCase().includes('bca') ||
                           admissionData.course_name?.toLowerCase().includes('bba') ||
                           admissionData.course_name?.toLowerCase().includes('mba') ||
                           admissionData.course_name?.toLowerCase().includes('mca') ||
                           admissionData.course_name?.toLowerCase().includes('b.com') ||
                           admissionData.course_name?.toLowerCase().includes('m.com') ||
                           admissionData.course_name?.toLowerCase().includes('b.a') ||
                           admissionData.course_name?.toLowerCase().includes('m.a') ||
                           admissionData.course_name?.toLowerCase().includes('m.sc') ||
                           admissionData.course_name?.toLowerCase().includes('d.voc') ||
                           admissionData.course_name?.toLowerCase().includes('b.voc');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <StudentHeader />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <StudentHeader />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8 overflow-x-auto pb-2">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
            }`}>1</div>
            <span className={`ml-2 mr-4 font-medium hidden sm:inline ${step >= 1 ? 'text-blue-600' : 'text-slate-400'}`}>Course</span>
          </div>
          <div className={`w-8 sm:w-16 h-1 ${step >= 1.5 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
          <div className="flex items-center ml-2 sm:ml-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= 1.5 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
            }`}>2</div>
            <span className={`ml-2 mr-4 font-medium hidden sm:inline ${step >= 1.5 ? 'text-blue-600' : 'text-slate-400'}`}>STT/RPL</span>
          </div>
          <div className={`w-8 sm:w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
          <div className="flex items-center ml-2 sm:ml-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
            }`}>3</div>
            <span className={`ml-2 mr-4 font-medium hidden sm:inline ${step >= 2 ? 'text-blue-600' : 'text-slate-400'}`}>Enrollment</span>
          </div>
          <div className={`w-8 sm:w-16 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
          <div className="flex items-center ml-2 sm:ml-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
            }`}>4</div>
            <span className={`ml-2 font-medium hidden sm:inline ${step >= 3 ? 'text-blue-600' : 'text-slate-400'}`}>Confirm</span>
          </div>
        </div>

        {/* Step 1: Select Course */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-blue-600" />
              Select a Course
            </h2>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search courses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border rounded-lg bg-white"
              >
                <option value="all">All Types</option>
                <option value="govt">Government (Free)</option>
                <option value="paid">Paid</option>
              </select>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border rounded-lg bg-white"
              >
                <option value="all">All Categories</option>
                <option value="professional">Professional</option>
                <option value="diploma">Diploma</option>
                <option value="degree">Degree</option>
                <option value="vocational">Vocational</option>
              </select>
            </div>

            {/* Course List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCourses.map(course => (
                <div 
                  key={course.id}
                  className="border-2 border-slate-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleCourseSelect(course)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-slate-800">{course.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      course.course_type === 'govt' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {course.course_type === 'govt' ? 'FREE' : 'PAID'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{course.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-slate-500">
                      <Clock className="h-4 w-4" />
                      {course.duration}
                    </span>
                    <span className="font-bold text-blue-600">
                      {course.fee === 0 ? 'Free' : (
                        <>
                          ₹{course.fee?.toLocaleString()}
                          {(course.category === 'degree' || course.category === 'diploma' || course.fee_type === 'yearly') && (
                            <span className="text-xs font-normal text-slate-500 ml-1">/Year</span>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  {/* Eligibility Badge */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    {(() => {
                      const eligibility = checkEligibility(course);
                      const minQual = course.minimum_qualification || '10th';
                      return (
                        <div className={`flex items-center justify-between text-xs ${
                          eligibility.isEligible ? 'text-emerald-600' : 'text-amber-600'
                        }`}>
                          <span className="flex items-center gap-1">
                            <Award className="h-3.5 w-3.5" />
                            Eligibility: {eligibilityLabels[minQual] || '10th Pass'}
                          </span>
                          {eligibility.isEligible ? (
                            <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                              <CheckCircle className="h-3 w-3" /> Eligible
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                              <AlertCircle className="h-3 w-3" /> Not Eligible
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>

            {filteredCourses.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <BookOpen className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>No courses found matching your criteria</p>
              </div>
            )}
          </div>
        )}

        {/* Step 1.5: Select Training Mode (STT/RPL) */}
        {step === 1.5 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <button 
              onClick={() => setStep(1)}
              className="text-blue-600 hover:underline mb-4 flex items-center gap-1"
            >
              ← Back to Course Selection
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-blue-600" />
              Select Training Mode
            </h2>
            <p className="text-slate-600 mb-6">
              Course: <strong>{admissionData.course_name}</strong>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* STT Option - Full Training */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  admissionData.training_mode === 'STT'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-blue-300 hover:shadow-md'
                }`}
                onClick={() => setAdmissionData({
                  ...admissionData,
                  training_mode: 'STT',
                  course_fee: admissionData.original_fee
                })}
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <BookOpen className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">STT - Full Training</h3>
                <p className="text-slate-600 mb-4 text-center text-sm">
                  Complete training program with classroom/online sessions, study materials, and certification exam.
                </p>
                <div className="text-center mb-4">
                  <span className="text-3xl font-bold text-blue-600">₹{admissionData.original_fee?.toLocaleString()}</span>
                  <p className="text-xs text-slate-500 mt-1">Full Course Fee</p>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    Complete Training Sessions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    Study Materials Included
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    Practical Assignments
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    Certification Exam
                  </li>
                </ul>
                {admissionData.training_mode === 'STT' && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-blue-600 font-medium">
                    <CheckCircle className="h-5 w-5" /> Selected
                  </div>
                )}
              </div>

              {/* RPL Option - Assessment Only */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  admissionData.training_mode === 'RPL'
                    ? 'border-purple-500 bg-purple-50 shadow-lg'
                    : 'border-slate-200 hover:border-purple-300 hover:shadow-md'
                }`}
                onClick={() => setAdmissionData({
                  ...admissionData,
                  training_mode: 'RPL',
                  course_fee: Math.round(admissionData.original_fee * 0.2)
                })}
              >
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <ClipboardCheck className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">RPL - Assessment Only</h3>
                <p className="text-slate-600 mb-4 text-center text-sm">
                  For experienced professionals. Skip training and directly appear for certification exam.
                </p>
                <div className="text-center mb-4">
                  <span className="text-3xl font-bold text-purple-600">₹{Math.round(admissionData.original_fee * 0.2)?.toLocaleString()}</span>
                  <p className="text-xs text-slate-500 mt-1">20% of Full Fee</p>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    Direct Certification Exam
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    Prior Learning Recognition
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    Same Valid Certificate
                  </li>
                  <li className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    No Training Sessions
                  </li>
                </ul>
                {admissionData.training_mode === 'RPL' && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-purple-600 font-medium">
                    <CheckCircle className="h-5 w-5" /> Selected
                  </div>
                )}
              </div>
            </div>

            {/* Continue Button */}
            <div className="mt-8 text-center">
              {admissionData.training_mode ? (
                <Button 
                  onClick={() => {
                    // RPL mode skips enrollment mode selection - go directly to confirm
                    if (admissionData.training_mode === 'RPL') {
                      setAdmissionData(prev => ({
                        ...prev,
                        enrollment_mode: 'online' // RPL is always online/direct
                      }));
                      setStep(3);
                    } else {
                      setStep(2);
                    }
                  }}
                  className={`px-8 py-3 text-lg ${
                    admissionData.training_mode === 'STT'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {admissionData.training_mode === 'RPL' 
                    ? 'Continue to Payment' 
                    : `Continue with ${admissionData.training_mode} Mode`}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 inline-flex items-center gap-2 text-amber-700">
                  <AlertCircle className="h-5 w-5" />
                  Please select STT or RPL mode to continue
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="mt-6 bg-slate-50 rounded-lg p-4 max-w-2xl mx-auto">
              <h4 className="font-semibold text-slate-700 mb-2">What is STT and RPL?</h4>
              <p className="text-sm text-slate-600">
                <strong>STT (Skill Training & Testing):</strong> Complete training program with classes, materials, and exam. You can choose online or training center mode.<br/>
                <strong>RPL (Recognition of Prior Learning):</strong> For those with existing skills/experience who want to directly get certified. Direct online assessment, no training center selection needed.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Choose Mode */}
        {step === 2 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <button 
              onClick={() => {
                // For Degree/Diploma, go back to course selection (Step 1)
                // For Professional courses, go back to Training Mode selection (Step 1.5)
                if (admissionData.training_mode === 'N/A') {
                  setStep(1);
                } else {
                  setStep(1.5);
                }
              }}
              className="text-blue-600 hover:underline mb-4 flex items-center gap-1"
            >
              ← Back to {admissionData.training_mode === 'N/A' ? 'Course Selection' : 'Training Mode Selection'}
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-blue-600" />
              Choose Enrollment Mode
            </h2>
            
            {/* Selected Course & Training Mode Summary */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <p className="text-sm text-slate-500">Course</p>
                  <p className="font-bold text-slate-800">{admissionData.course_name}</p>
                </div>
                {/* Only show Training Mode for Professional courses (not for Degree/Diploma) */}
                {admissionData.training_mode && admissionData.training_mode !== 'N/A' && (
                  <>
                    <div className="h-8 w-px bg-slate-300 hidden sm:block"></div>
                    <div>
                      <p className="text-sm text-slate-500">Training Mode</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                        admissionData.training_mode === 'STT'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {admissionData.training_mode === 'STT' ? (
                          <><BookOpen className="h-3.5 w-3.5" /> STT - Full Training</>
                        ) : (
                          <><ClipboardCheck className="h-3.5 w-3.5" /> RPL - Assessment Only</>
                        )}
                      </span>
                    </div>
                  </>
                )}
                <div className="h-8 w-px bg-slate-300 hidden sm:block"></div>
                <div>
                  <p className="text-sm text-slate-500">Fee</p>
                  <p className={`font-bold ${
                    admissionData.training_mode === 'N/A' 
                      ? 'text-slate-700'
                      : admissionData.training_mode === 'STT' 
                        ? 'text-blue-600' 
                        : 'text-purple-600'
                  }`}>
                    ₹{admissionData.course_fee?.toLocaleString()}
                    {admissionData.training_mode === 'RPL' && (
                      <span className="text-xs text-slate-500 ml-1">(20% of ₹{admissionData.original_fee?.toLocaleString()})</span>
                    )}
                    {(admissionData.course_category === 'degree' || admissionData.course_category === 'diploma') && (
                      <span className="text-xs text-slate-500 ml-1">/Year</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* For Degree/Diploma - Only Distance Mode */}
            {isDegreeOrDiploma ? (
              <div className="max-w-md mx-auto">
                <div 
                  className="border-2 border-blue-500 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-white shadow-lg cursor-pointer hover:shadow-xl transition-all"
                  onClick={() => handleModeSelect('distance')}
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Globe className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">Distance Education Mode</h3>
                  <p className="text-slate-600 mb-4 text-center">
                    Degree and Diploma programs are available only in Distance Education mode through our affiliated universities.
                  </p>
                  
                  {/* Yearly Fee Display */}
                  <div className="bg-blue-100 rounded-lg p-4 mb-4 text-center">
                    <p className="text-sm text-blue-700 font-medium mb-1">Course Fee</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ₹{admissionData.original_fee?.toLocaleString()}
                      <span className="text-sm font-normal text-blue-500">/Year</span>
                    </p>
                    <p className="text-xs text-blue-600 mt-1">* Fee is charged on yearly basis</p>
                  </div>
                  
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      UGC-DEB Approved Universities
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Study at your own pace
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Online study materials & support
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Valid for Govt. Jobs
                    </li>
                  </ul>
                  <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                    Continue with Distance Mode <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-slate-500 text-center mt-4">
                  Note: Degree & Diploma courses are conducted through Distance Education only.
                </p>
              </div>
            ) : (
              /* For Professional Courses - Online & Training Center Options */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Online Mode */}
              <div 
                className="border-2 border-purple-200 rounded-xl p-6 hover:border-purple-500 hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-purple-50 to-white"
                onClick={() => handleModeSelect('online')}
              >
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Globe className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Online Enrollment</h3>
                <p className="text-slate-600 mb-4">
                  Enroll directly through Iqra Skills. Study at your own pace with online resources and support.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Direct enrollment
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Online study materials
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Flexible timing
                  </li>
                </ul>
                <Button className="w-full mt-4 bg-purple-600 hover:bg-purple-700">
                  Select Online <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* Training Center Mode */}
              <div className="border-2 border-orange-200 rounded-xl p-6 bg-gradient-to-br from-orange-50 to-white">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <Building2 className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Training Center</h3>
                <p className="text-slate-600 mb-4">
                  Join through a local training center for hands-on classroom experience.
                </p>
                
                {/* Center Search & Filters */}
                <div className="space-y-3 mb-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search center name, city..."
                      value={centerSearchTerm}
                      onChange={(e) => setCenterSearchTerm(e.target.value)}
                      className="pl-10 text-sm"
                    />
                  </div>
                  
                  {/* State & District Filters */}
                  <div className="flex gap-2">
                    <select
                      value={centerStateFilter}
                      onChange={(e) => { setCenterStateFilter(e.target.value); setCenterDistrictFilter('all'); }}
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                    >
                      <option value="all">All States</option>
                      {uniqueStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    <select
                      value={centerDistrictFilter}
                      onChange={(e) => setCenterDistrictFilter(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                    >
                      <option value="all">All Districts</option>
                      {uniqueDistricts.map(district => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Clear filters button */}
                  {(centerSearchTerm || centerStateFilter !== 'all' || centerDistrictFilter !== 'all') && (
                    <button
                      onClick={() => { setCenterSearchTerm(''); setCenterStateFilter('all'); setCenterDistrictFilter('all'); }}
                      className="text-xs text-orange-600 hover:underline"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
                
                {/* Student's Location Info */}
                {studentProfile?.district && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-green-700">
                      Showing centers near <strong>{studentProfile.district}, {studentProfile.state}</strong> first
                    </span>
                  </div>
                )}
                
                {sortedAndFilteredCenters.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {sortedAndFilteredCenters.map((center, index) => {
                      const isNearby = studentProfile?.district && 
                        (center.district || '').toLowerCase() === (studentProfile.district || '').toLowerCase() &&
                        (center.state || '').toLowerCase() === (studentProfile.state || '').toLowerCase();
                      
                      return (
                        <div 
                          key={center.id}
                          className={`p-3 border rounded-lg hover:border-orange-400 hover:bg-orange-50 cursor-pointer transition-all ${
                            isNearby ? 'border-green-400 bg-green-50' : ''
                          }`}
                          onClick={() => handleModeSelect('center', center)}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-slate-800">{center.center_name || center.owner_name}</p>
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {center.district || center.city}, {center.state}
                              </p>
                              <p className="text-xs text-orange-600">{center.center_code}</p>
                            </div>
                            {isNearby && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                Near You
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                    {centerSearchTerm || centerStateFilter !== 'all' || centerDistrictFilter !== 'all' 
                      ? 'No centers found matching your search. Try different filters.'
                      : 'No training centers available. Please choose Online enrollment.'}
                  </p>
                )}
                
                {/* Total centers count */}
                {sortedAndFilteredCenters.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Showing {sortedAndFilteredCenters.length} center{sortedAndFilteredCenters.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            )}
          </div>
        )}

        {/* Step 2.5: Fee Installment Selection (STT + Professional only) */}
        {step === 2.5 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl mx-auto">
            <button 
              onClick={() => setStep(2)}
              className="text-blue-600 hover:underline mb-4 flex items-center gap-1"
            >
              ← Back to Enrollment Mode Selection
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
              <Wallet className="h-6 w-6 text-purple-600" />
              Fee Installment Option / शुल्क किस्त विकल्प
            </h2>
            <p className="text-slate-600 mb-6">Choose how you want to pay the course fee</p>
            
            {/* Course Summary */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6 border border-purple-100">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-slate-500">Course</p>
                  <p className="font-bold text-slate-800">{admissionData.course_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Training Mode</p>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                    <BookOpen className="h-3.5 w-3.5" /> STT - Full Training
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Course Fee</p>
                  <p className="text-2xl font-bold text-purple-600">₹{admissionData.course_fee?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Installment Options */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* One Installment Option */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  admissionData.installment_plan === 'one'
                    ? 'border-green-500 bg-green-50 shadow-lg'
                    : 'border-slate-200 hover:border-green-300 hover:shadow-md'
                }`}
                onClick={() => setAdmissionData({...admissionData, installment_plan: 'one'})}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-green-600" />
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    admissionData.installment_plan === 'one'
                      ? 'border-green-500 bg-green-500'
                      : 'border-slate-300'
                  }`}>
                    {admissionData.installment_plan === 'one' && (
                      <CheckCircle className="h-4 w-4 text-white" />
                    )}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">One Installment</h3>
                <p className="text-slate-600 mb-4 text-sm">Pay full fee at once and complete your enrollment instantly.</p>
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-sm text-slate-500">Pay Now</p>
                  <p className="text-2xl font-bold text-green-600">₹{admissionData.course_fee?.toLocaleString()}</p>
                  <p className="text-xs text-green-600 mt-1">✓ No remaining balance</p>
                </div>
              </div>

              {/* Two Installments Option */}
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  admissionData.installment_plan === 'two'
                    ? 'border-purple-500 bg-purple-50 shadow-lg'
                    : 'border-slate-200 hover:border-purple-300 hover:shadow-md'
                }`}
                onClick={() => setAdmissionData({...admissionData, installment_plan: 'two'})}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Wallet className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    admissionData.installment_plan === 'two'
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-slate-300'
                  }`}>
                    {admissionData.installment_plan === 'two' && (
                      <CheckCircle className="h-4 w-4 text-white" />
                    )}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Two Installments</h3>
                <p className="text-slate-600 mb-4 text-sm">Split your fee in 60:40 ratio. Pay first installment now.</p>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Installment 1 (60%)</span>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Pay Now</span>
                    </div>
                    <p className="text-xl font-bold text-purple-600">₹{Math.round(admissionData.course_fee * 0.6).toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Installment 2 (40%)</span>
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Pay Later</span>
                    </div>
                    <p className="text-lg font-bold text-slate-600">₹{(admissionData.course_fee - Math.round(admissionData.course_fee * 0.6)).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <Button
              onClick={() => handleInstallmentSelect(admissionData.installment_plan)}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 py-6 text-lg"
            >
              Continue with {admissionData.installment_plan === 'two' ? 'Two Installments' : 'Full Payment'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            {/* Info Note */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Two Installment option mein, 2nd installment course ke beech mein pay karna hoga. 
                2nd installment pay na karne par certificate issue nahi hoga.
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-xl mx-auto">
            <button 
              onClick={() => {
                // For Degree/Diploma, go back to enrollment mode (Step 2)
                // RPL mode came directly from step 1.5, go back there
                // STT + Professional with installment goes to step 2.5
                // STT mode goes back to Step 2
                const isProfessional = admissionData.course_category === 'professional' || admissionData.course_type === 'professional';
                if (admissionData.training_mode === 'N/A') {
                  setStep(2);
                } else if (admissionData.training_mode === 'RPL') {
                  setStep(1.5);
                } else if (admissionData.training_mode === 'STT' && isProfessional) {
                  setStep(2.5);
                } else {
                  setStep(2);
                }
              }}
              className="text-blue-600 hover:underline mb-4 flex items-center gap-1"
            >
              ← Back to {admissionData.training_mode === 'RPL' ? 'Training Mode' : 
                ((admissionData.training_mode === 'STT' && (admissionData.course_category === 'professional' || admissionData.course_type === 'professional')) ? 'Installment' : 'Enrollment Mode')} Selection
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-600" />
              Confirm Admission
            </h2>

            <div className="space-y-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <Label className="text-slate-500">Course</Label>
                <p className="text-lg font-bold text-slate-800">{admissionData.course_name}</p>
              </div>
              
              {/* Training Mode (STT/RPL) - Only show for Professional courses */}
              {admissionData.training_mode && admissionData.training_mode !== 'N/A' && (
                <div className={`rounded-lg p-4 ${
                  admissionData.training_mode === 'STT' ? 'bg-blue-50' : 'bg-purple-50'
                }`}>
                  <Label className={admissionData.training_mode === 'STT' ? 'text-blue-600' : 'text-purple-600'}>
                    Training Mode
                  </Label>
                  <p className={`text-lg font-bold flex items-center gap-2 ${
                    admissionData.training_mode === 'STT' ? 'text-blue-700' : 'text-purple-700'
                  }`}>
                    {admissionData.training_mode === 'STT' ? (
                      <>
                        <BookOpen className="h-5 w-5" />
                        STT - Full Training Program
                      </>
                    ) : (
                      <>
                        <ClipboardCheck className="h-5 w-5" />
                        RPL - Assessment Only (Direct Exam)
                      </>
                    )}
                  </p>
                </div>
              )}
              
              <div className="bg-slate-50 rounded-lg p-4">
                <Label className="text-slate-500">Enrollment Mode</Label>
                <p className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  {admissionData.enrollment_mode === 'online' ? (
                    <>
                      <Globe className="h-5 w-5 text-purple-600" />
                      Online Enrollment
                    </>
                  ) : admissionData.enrollment_mode === 'distance' ? (
                    <>
                      <Globe className="h-5 w-5 text-blue-600" />
                      Distance Education
                    </>
                  ) : (
                    <>
                      <Building2 className="h-5 w-5 text-orange-600" />
                      {admissionData.center_name}
                    </>
                  )}
                </p>
              </div>
              
              <div className={`rounded-lg p-4 ${
                admissionData.training_mode === 'N/A' 
                  ? 'bg-slate-50'
                  : admissionData.training_mode === 'STT' 
                    ? 'bg-blue-50' 
                    : 'bg-purple-50'
              }`}>
                <Label className={
                  admissionData.training_mode === 'N/A'
                    ? 'text-slate-600'
                    : admissionData.training_mode === 'STT' 
                      ? 'text-blue-600' 
                      : 'text-purple-600'
                }>
                  Course Fee
                  {(admissionData.course_category === 'degree' || admissionData.course_category === 'diploma') && (
                    <span className="text-xs font-normal ml-1">(Yearly)</span>
                  )}
                </Label>
                <p className={`text-2xl font-bold ${
                  admissionData.training_mode === 'N/A'
                    ? 'text-slate-700'
                    : admissionData.training_mode === 'STT' 
                      ? 'text-blue-700' 
                      : 'text-purple-700'
                }`}>
                  {admissionData.course_fee === 0 ? 'FREE' : (
                    <>
                      ₹{admissionData.course_fee?.toLocaleString()}
                      {(admissionData.course_category === 'degree' || admissionData.course_category === 'diploma') && (
                        <span className="text-sm font-normal text-slate-500">/Year</span>
                      )}
                    </>
                  )}
                </p>
                {admissionData.training_mode === 'RPL' && admissionData.original_fee > 0 && (
                  <p className="text-sm text-slate-600 mt-1">
                    RPL Fee (20% of ₹{admissionData.original_fee?.toLocaleString()})
                  </p>
                )}
                {admissionData.course_fee > 0 && !showInstallmentOption && (
                  <p className="text-sm text-slate-500 mt-1">Payment will be collected after enrollment</p>
                )}
              </div>

              {/* Installment Summary for STT + Professional */}
              {showInstallmentOption && admissionData.course_fee > 0 && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                  <Label className="text-purple-700 flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    Fee Installment Plan / किस्त योजना
                  </Label>
                  <p className="font-bold text-purple-800 mb-3">
                    {admissionData.installment_plan === 'two' ? 'Two Installments (60:40)' : 'One Installment (Full Payment)'}
                  </p>
                  
                  {admissionData.installment_plan === 'two' ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center bg-white rounded-lg p-3 border border-purple-200">
                        <div>
                          <span className="text-sm text-slate-600">Installment 1 (60%)</span>
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Pay Now</span>
                        </div>
                        <span className="text-lg font-bold text-green-600">
                          ₹{Math.round(admissionData.course_fee * 0.6).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div>
                          <span className="text-sm text-slate-600">Installment 2 (40%)</span>
                          <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Pay Later</span>
                        </div>
                        <span className="text-lg font-bold text-amber-600">
                          ₹{(admissionData.course_fee - Math.round(admissionData.course_fee * 0.6)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Full Payment</span>
                        <span className="text-lg font-bold text-green-600">
                          ₹{admissionData.course_fee?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-4">
                <Label className="text-slate-500">Student</Label>
                <p className="font-medium text-slate-800">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>

            <Button 
              onClick={handleSubmitAdmission}
              disabled={submitting}
              className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
            >
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </div>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Confirm Admission
                </>
              )}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdmissionPage;
